import React from "react";
import { useLang } from "../../context/LanguageContext";
import { ADDRESS, ADDRESS_SUITE } from "../../lib/constants";
import { MapPin, Plane, Waves, Train, Navigation } from "lucide-react";
import { Reveal } from "./Reveal";

const pinIcons = [Plane, Waves, Train, MapPin, MapPin, MapPin];

export const Location = () => {
  const { t } = useLang();

  return (
    <section
      id="location"
      data-testid="location-section"
      className="relative py-24 md:py-40 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.35em] text-amber-800/80">
                — {t.location.overline}
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-6 font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-stone-900">
                {t.location.title}
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-8 text-stone-600 font-light leading-relaxed text-lg max-w-md">
                {t.location.description}
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 border-l-2 border-amber-700/60 pl-6">
                <div className="text-[11px] uppercase tracking-[0.25em] text-stone-500 mb-2">
                  {t.contact.address}
                </div>
                <div className="font-heading text-2xl md:text-3xl text-stone-900 leading-snug">
                  {ADDRESS}
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={120}>
              <div className="relative aspect-[16/10] bg-stone-200 overflow-hidden border border-stone-200">
                <iframe
                  title="Annette Rooms & Suite maXimo — Google Maps"
                  data-testid="google-maps-iframe"
                  src="https://www.google.com/maps?saddr=Via+Domenico+Renna+14,+Bari+Palese,+Italy&daddr=Via+Domenico+Renna+12,+Bari+Palese,+Italy&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
              {t.location.pins.map((pin, i) => {
                const Icon = pinIcons[i];
                return (
                  <Reveal key={i} delay={60 * i}>
                    <div
                      data-testid={`location-pin-${i}`}
                      className="bg-[#FDFBF7] p-6 flex items-start gap-3"
                    >
                      <Icon className="w-5 h-5 text-amber-800/80 mt-1 stroke-[1.3] shrink-0" />
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">
                          {pin.label}
                        </div>
                        <div className="font-heading text-xl md:text-2xl text-stone-900 mt-1">
                          {pin.value}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={240}>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                    ADDRESS
                  )}&travelmode=driving`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="directions-cta"
                  className="group inline-flex items-center justify-center gap-3 bg-stone-900 text-white px-6 py-4 text-xs uppercase tracking-[0.25em] hover:bg-amber-800 transition-colors duration-300"
                >
                  <Navigation className="w-4 h-4 transition-transform group-hover:-rotate-12" />
                  {t.location.directions}
                </a>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    ADDRESS
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-maps-link"
                  className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] text-stone-900 border border-stone-300 px-6 py-4 hover:border-stone-900 transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  {t.location.openMap}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
